(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./ai-chat-core.js'), null);
    return;
  }

  if (root) {
    root.MarineMaxAIChatAnalytics = factory(root.MarineMaxAIChatCore, root);
  }
})(typeof window !== 'undefined' ? window : null, function (core, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the analytics module');
  }

  function createAnalytics(options = {}) {
    const windowRef = options.window || root;
    const setTimer =
      options.setTimeout ||
      (windowRef && windowRef.setTimeout
        ? windowRef.setTimeout.bind(windowRef)
        : globalThis.setTimeout.bind(globalThis));
    const clearTimer =
      options.clearTimeout ||
      (windowRef && windowRef.clearTimeout
        ? windowRef.clearTimeout.bind(windowRef)
        : globalThis.clearTimeout.bind(globalThis));
    const measurementId =
      options.measurementId || core.constants.MARINEMAX_GA4_MEASUREMENT_ID;
    const ga4TimeoutMs =
      options.ga4TimeoutMs ?? core.constants.AI_CHAT_GA4_GET_TIMEOUT_MS;

    if (!windowRef) {
      throw new Error('A window implementation is required by the analytics module');
    }

    function getPageLocation() {
      return windowRef.location ? windowRef.location.href : '';
    }

    function getDataLayerApi() {
      return options.dataLayerApi || windowRef.MarineMaxDataLayer;
    }

    function pushToDataLayer(payload) {
      windowRef.dataLayer = windowRef.dataLayer || [];
      windowRef.dataLayer.push(payload);
    }

    function getGa4Value(fieldName) {
      return new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          if (timeoutId !== null) clearTimer(timeoutId);
          resolve(value == null ? '' : String(value));
        };
        timeoutId = setTimer(() => finish(''), ga4TimeoutMs);

        try {
          const gtag = options.gtag || windowRef.gtag;
          if (typeof gtag !== 'function') {
            finish('');
            return;
          }
          gtag('get', measurementId, fieldName, finish);
        } catch (_) {
          finish('');
        }
      });
    }

    async function getGa4MessageContext() {
      const pageLocation = getPageLocation();
      const [clientId, sessionId] = await Promise.all([
        getGa4Value('client_id'),
        getGa4Value('session_id'),
      ]);

      return {
        ga_client_id: clientId || null,
        ga_session_id: sessionId || null,
        ga_page_location: pageLocation,
      };
    }

    function pushLeadEvents(payload) {
      try {
        const dataLayerApi = getDataLayerApi();
        if (
          dataLayerApi &&
          typeof dataLayerApi.pushGenerateLead === 'function'
        ) {
          if (typeof dataLayerApi.pushFormSubmit === 'function') {
            dataLayerApi.pushFormSubmit(payload);
          }
          dataLayerApi.pushGenerateLead(payload);
          return true;
        }

        pushToDataLayer(
          Object.assign(
            {
              event: 'form_submit',
              page_location: getPageLocation(),
            },
            payload,
          ),
        );
        pushToDataLayer(
          Object.assign(
            {
              event: 'generate_lead',
              page_location: getPageLocation(),
            },
            payload,
          ),
        );
        return true;
      } catch (_) {
        return false;
      }
    }

    function pushChatInteraction(payload) {
      try {
        const dataLayerApi = getDataLayerApi();
        if (
          dataLayerApi &&
          typeof dataLayerApi.pushChatInteraction === 'function'
        ) {
          dataLayerApi.pushChatInteraction(payload);
          return true;
        }

        pushToDataLayer({
          event: 'chat_interaction',
          chat_tool: payload.chat_tool,
          action: payload.action,
          prompt_label: payload.prompt_label,
          prompt_type: payload.prompt_type,
          page_location: getPageLocation(),
        });
        return true;
      } catch (_) {
        return false;
      }
    }

    return Object.freeze({
      getGa4MessageContext,
      getGa4Value,
      pushChatInteraction,
      pushLeadEvents,
    });
  }

  return Object.freeze({
    createAnalytics,
  });
});
